import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { Calculator, UserRound } from 'lucide-react';
import { ModeCalculTarification, ReferentialItemDto } from 'api/funnel';
import { funnelReferentialsAPI, funnelsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { CheckboxComponent } from 'components/forms/Checkbox';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { useCommissionBeneficiaryOptions } from '../hooks/useActorOptions';
import { useBankAccountOptions } from '../hooks/useBankAccountOptions';
import { ExpressionVariable } from '../components/expression/ExpressionBuilder';
import { ModalShell, ModalSection } from 'back-office/shared/components/ModalShell';
import {
  TarifFields,
  extractTarifValues,
  prefixTarifValues,
  tarifFromFormValues,
  tarifToFormValues,
} from '../components/TarifFields';

interface CommissionFormValues extends Record<string, unknown> {
  natureRemunerationId: string;
  tarifModeCalcul: ModeCalculTarification;
  tarifMontant: number | '';
  tarifExpression: string;
  useConnectedBroker: boolean;
  beneficiairePersonneId: string;
  compteBancaireId: string;
}

const validationSchema = Yup.object().shape({
  natureRemunerationId: Yup.string().required('Le type de la commission est requis'),
  beneficiairePersonneId: Yup.string().when('useConnectedBroker', {
    is: false,
    then: (schema) => schema.required('Le bénéficiaire est requis'),
    otherwise: (schema) => schema.nullable(),
  }),
});

interface GuaranteeCommissionModalProps {
  garantieTunnelId: string;
  /** Absent en création. */
  commissionId?: string;
  guaranteeLabel: string;
  variables: ExpressionVariable[];
  onSuccess: () => void;
}

/** Création et modification d'une commission portée par une garantie du tunnel. */
export const GuaranteeCommissionModal: FC<GuaranteeCommissionModalProps> = ({
  garantieTunnelId,
  commissionId,
  guaranteeLabel,
  variables,
  onSuccess,
}) => {
  const formikRef = useRef<FormikProps<CommissionFormValues>>(null);
  const { closeDialog } = useDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(commissionId);

  const fetchCommission = useCallback(
    () =>
      funnelsAPI.getGuaranteeCommission({
        commissionGarantieTunnelId: commissionId as string,
      }),
    [commissionId]
  );
  const commission = useAsyncResource(fetchCommission, isEditMode);

  const fetchNatures = useCallback(() => funnelReferentialsAPI.getRemunerationNatures(), []);
  const natures = useAsyncResource<ReferentialItemDto[]>(fetchNatures);

  const initialValues = useMemo(
    () =>
      ({
        natureRemunerationId: commission.data?.natureRemunerationId ?? '',
        ...prefixTarifValues('tarif', tarifToFormValues(commission.data?.tarif)),
        useConnectedBroker: commission.data?.isCourtierConnecte ?? false,
        beneficiairePersonneId: commission.data?.beneficiairePersonneId ?? '',
        compteBancaireId: commission.data?.compteBancaireId ?? '',
      }) as CommissionFormValues,
    [commission.data]
  );

  const handleSubmit = async (values: CommissionFormValues) => {
    setIsSubmitting(true);
    try {
      const command = {
        natureRemunerationId: values.natureRemunerationId,
        tarif: tarifFromFormValues(extractTarifValues('tarif', values)),
        // Quand le courtier connecté encaisse, le back ignore le bénéficiaire :
        // on l'envoie à null plutôt que de laisser un titulaire fantôme.
        beneficiairePersonneId: values.useConnectedBroker ? null : values.beneficiairePersonneId,
        compteBancaireId: values.useConnectedBroker ? null : values.compteBancaireId || null,
      };

      const response = isEditMode
        ? await funnelsAPI.updateGuaranteeCommission({
            commissionGarantieTunnelId: commissionId as string,
            updateCommissionGarantieCommand: command,
          })
        : await funnelsAPI.createGuaranteeCommission({
            createCommissionGarantieCommand: { ...command, garantieTunnelId },
          });

      if (response.isSuccess) {
        toast.success(isEditMode ? 'Commission modifiée' : 'Commission ajoutée');
        onSuccess();
        closeDialog();
      } else {
        toast.error(response.errorMessage || "L'enregistrement a échoué");
      }
    } catch (error) {
      console.error('Enregistrement de la commission impossible', error);
      // Le back explique la formule refusée ou la variable inconnue : on relaie son message.
      const parsed = await parseApiError(error);
      toast.error(parsed.message || "L'enregistrement a échoué");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      <ModalShell
        title={isEditMode ? 'Modifier la commission' : 'Ajouter une commission'}
        subtitle={`Garantie ${guaranteeLabel}`}
        isLoading={commission.isLoading || natures.isLoading}
        submit={{
          label: 'Enregistrer',
          onClick: () => formikRef.current?.submitForm(),
          disabled: isSubmitting,
        }}
      >
        <Form>
          <ModalSection title="Type et tarification" icon={<Calculator className="h-4 w-4" />}>
            <div className="flex flex-col gap-4">
              <FormikDropdown
                name="natureRemunerationId"
                label="Type de la commission"
                required
                items={(natures.data ?? []).map((nature) => ({
                  key: nature.id,
                  label: nature.libelle || nature.code || 'Sans libellé',
                }))}
                placeholder="Sélectionner un type"
              />
              <TarifFields
                prefix="tarif"
                label="Mode de calcul"
                required
                variables={variables}
              />
            </div>
          </ModalSection>

          <ModalSection title="Bénéficiaire" icon={<UserRound className="h-4 w-4" />}>
            <BeneficiaryFields natures={natures.data ?? []} />
          </ModalSection>
        </Form>
      </ModalShell>
    </Formik>
  );
};

const BeneficiaryFields: FC<{ natures: ReferentialItemDto[] }> = ({ natures }) => {
  const { values, setFieldValue } = useFormikContext<CommissionFormValues>();

  const nature = natures.find((item) => item.id === values.natureRemunerationId);
  const beneficiaries = useCommissionBeneficiaryOptions(nature?.code);
  const bankAccounts = useBankAccountOptions(
    values.useConnectedBroker ? null : values.beneficiairePersonneId
  );

  useEffect(() => {
    if (!values.compteBancaireId && bankAccounts.defaultAccountId) {
      void setFieldValue('compteBancaireId', bankAccounts.defaultAccountId);
    }
  }, [bankAccounts.defaultAccountId, values.compteBancaireId, setFieldValue]);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <CheckboxComponent
          name="useConnectedBroker"
          label="Le courtier connecté est le bénéficiaire"
          checked={values.useConnectedBroker}
          onChange={(checked) => setFieldValue('useConnectedBroker', checked)}
        />
        <p className="ml-6 mt-0.5 text-xs text-gray-400">
          Si cochée, le courtier qui contractualise sera le bénéficiaire de cette commission.
        </p>
      </div>

      {!values.useConnectedBroker && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormikDropdown
            name="beneficiairePersonneId"
            label="Bénéficiaire"
            required
            items={(beneficiaries.data ?? []).map((actor) => ({
              key: actor.key,
              label: actor.label,
              trailingLabel: actor.role,
            }))}
            placeholder={beneficiaries.isLoading ? 'Chargement…' : 'Sélectionner un bénéficiaire'}
          />
          <FormikDropdown
            name="compteBancaireId"
            label="Compte bancaire"
            items={bankAccounts.options}
            disabled={!values.beneficiairePersonneId}
            placeholder={
              values.beneficiairePersonneId
                ? 'Sélectionner un compte'
                : "Sélectionner d'abord un bénéficiaire"
            }
          />
        </div>
      )}
    </div>
  );
};
